// @ts-check
/** @typedef {import('./actionsT.ts').Action} Action */
/** @typedef {import('./reducer.js').FormState} FormState */
/** @typedef {import("./reducer.js").FormFields} FormFields */

import { dbg } from "../../../../utils/general.utils.js"
import {
  UPDATE_DATE,
  RESET_DATE,
  SET_PROJECTS_LOADING,
  SET_PROJECTS_OPTIONS,
  SET_PROJECTS_ERROR,
  RESET_PROJECT,
  UPDATE_CATEGORY,
  RESET_CATEGORY,
  UPDATE_MODULE,
  RESET_MODULE,
  UPDATE_COMMENT,
  RESET_COMMENT,
  UPDATE_BILLED_TIME_REMOVAL,
  RESET_BILLED_TIME_REMOVAL,
  RESET_FIELDS,
  SET_CATEGORIES_LOADING,
  SET_CATEGORIES_OPTIONS,
  SET_CATEGORY_ERROR,
  SET_FETCHING_MODULES,
  SET_FETCHED_MODULES,
  SET_FETCHING_MODULES_ERROR,
  UPDATE_PROJECT,
  SET_INITIALLY_SELECTED_DATES,
  UPDATE_TIME_DIFFERENCE_COMMENT,
  RESET_TIME_DIFFERENCE_COMMENT,
} from "./actions.js"

/**
 * @param {FormState} state
 * @param {Action} action
 * @returns {FormState}
 */
export function formReducer(state, action) {
  dbg(() => {
    console.info(
      "Bulk edit tasks reducer: Dispatching action",
      action.type,
      ". payload:",
      // @ts-ignore
      action.payload
    )
  })

  /**
   * @param {boolean} selectedProjectUsesSpecialCategories
   * @returns {FormState} */
  const resetFields = (selectedProjectUsesSpecialCategories) => ({
    status: "initial",
    fields: {
      project: undefined,
      selectedProjectUsesSpecialCategories,
      category: undefined,
      module: undefined,
      date: undefined,
      comment: "",
      shouldDeleteBilledTime: false,
      timeDifferenceComment: "",
    },

    initiallySelectedDates: state.initiallySelectedDates,

    availableProjects: state.availableProjects,
    projectsError: undefined,
    isFetchingProjects: false,
    isFetchingCategories: false,
    availableCategories: state.availableCategories,
    categoriesError: undefined,
    isFetchingModules: false,
    availableModules: state.availableModules,
    modulesError: undefined,
  })

  /**
   * @param {FormFields} fields
   * @returns {"initial" | "dirty"}
   */
  const getNewStatus = (fields) => {
    const cleanedFormValues =
      fields.project === undefined &&
      fields.category === undefined &&
      fields.module === undefined &&
      fields.date === undefined &&
      fields.comment === "" &&
      fields.shouldDeleteBilledTime === false &&
      fields.timeDifferenceComment === ""

    return cleanedFormValues ? "initial" : "dirty"
  }

  /**
   * @param {(fields: FormFields) => FormFields} updater
   * @param {FormState} [prevState]
   * @returns {FormState}
   */
  const changeFields = (updater, prevState) => {
    const newFields = updater(prevState?.fields || state.fields)

    return {
      ...(prevState || state),
      status: getNewStatus(newFields),
      fields: newFields,
    }
  }

  switch (action.type) {
    case UPDATE_PROJECT: {
      const newState = { ...state }
      let selectedInt = false
      selectedInt =
        state.availableProjects.find((p) =>
          p.items.find((i) => i.projectName === action.payload.project)
        )?.items[0].isBillable === 0

      if (selectedInt) {
        newState.fields.shouldDeleteBilledTime = false
      }

      newState.fields.project = action.payload.project
      newState.fields.selectedProjectUsesSpecialCategories =
        action.payload.projectUsesSpecialCategories
      newState.fields.module = undefined
      newState.status = getNewStatus(newState.fields)
      return newState
    }

    case RESET_PROJECT:
      return changeFields((fields) => ({
        ...fields,
        project: undefined,
        module: undefined,
        category: undefined,
      }))

    case UPDATE_CATEGORY: {
      const newCategory = action.payload.category
      return changeFields((fields) => ({
        ...fields,
        category: newCategory,
      }))
    }

    case RESET_CATEGORY:
      return changeFields((fields) => ({ ...fields, category: undefined }))

    case UPDATE_MODULE: {
      const newModule = action.payload.module
      return changeFields((fields) => ({ ...fields, module: newModule }))
    }

    case RESET_MODULE:
      return changeFields((fields) => ({ ...fields, module: undefined }))

    case UPDATE_DATE: {
      const newDate = action.payload.date
      return changeFields((fields) => ({ ...fields, date: newDate }))
    }

    case RESET_DATE:
      return changeFields((fields) => ({ ...fields, date: undefined }))

    case UPDATE_COMMENT: {
      const newComment = action.payload.comment
      return changeFields((fields) => ({ ...fields, comment: newComment }))
    }

    case RESET_COMMENT:
      return changeFields((fields) => ({ ...fields, comment: "" }))

    case UPDATE_BILLED_TIME_REMOVAL: {
      const newVal = action.payload.value
      return changeFields((fields) => ({
        ...fields,
        shouldDeleteBilledTime: newVal,
        timeDifferenceComment:
          newVal === false ? "" : fields.timeDifferenceComment,
      }))
    }

    case RESET_BILLED_TIME_REMOVAL:
      return changeFields((fields) => ({
        ...fields,
        shouldDeleteBilledTime: false,
        timeDifferenceComment: "",
      }))

    case UPDATE_TIME_DIFFERENCE_COMMENT: {
      if (!state.fields.shouldDeleteBilledTime) {
        return state
      }

      const newComment = action.payload.comment
      return changeFields((fields) => ({
        ...fields,
        timeDifferenceComment: newComment,
      }))
    }

    case RESET_TIME_DIFFERENCE_COMMENT:
      return changeFields((fields) => ({
        ...fields,
        timeDifferenceComment: "",
      }))

    case SET_INITIALLY_SELECTED_DATES: {
      const dates = action.payload.dates
      return { ...state, initiallySelectedDates: dates }
    }

    // Fetching data

    case SET_CATEGORIES_LOADING:
      return { ...state, isFetchingCategories: action.payload.isLoading }

    case SET_CATEGORIES_OPTIONS: {
      const categories = action.payload.options
      return { ...state, availableCategories: categories }
    }

    case SET_CATEGORY_ERROR:
      return { ...state, categoriesError: action.payload.error }

    case SET_FETCHING_MODULES:
      return { ...state, isFetchingModules: action.payload.isLoading }

    case SET_FETCHED_MODULES: {
      const modules = action.payload.options
      return { ...state, availableModules: modules }
    }

    case SET_FETCHING_MODULES_ERROR:
      return { ...state, modulesError: action.payload.error }

    case SET_PROJECTS_LOADING:
      return { ...state, isFetchingProjects: action.payload.isLoading }

    case SET_PROJECTS_OPTIONS: {
      const projects = action.payload.options
      return { ...state, availableProjects: projects }
    }

    case SET_PROJECTS_ERROR:
      return { ...state, projectsError: action.payload.error }

    case RESET_FIELDS:
      return resetFields(
        action.payload.initiallySelectedProjectsAllHaveSpecialCategories
      )

    default:
      return state
  }
}
